/**
 * Underwriting Question Agent for Ameen Insurance Assistant
 *
 * Collects 7 underwriting answers via voice/popup, then routes to vehicle inspection.
 *
 * TOOLS (from backend):
 * - show_vehicle_usage_choice, show_had_accident_choice, show_night_parking_choice
 * - show_violations_input, show_tracking_device_choice, show_vehicle_financing_choice
 * - show_number_of_drivers_input
 * - go_to_vehicle_inspection_agent: Route to vehicle inspection (pass flow_state)
 * - go_to_policy_agent, go_to_router_agent, search_faq
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender, UIStateManager } from '../types';
import { noEntryRequirements } from '../guardrails';
import { logger } from '../../utils/logger';
import { handleSearchFAQ } from './RouterAgent';

const WAIT_MSG = 'Popup displayed. Waiting for [system_ui] signal.';

export function createUnderWritingQuestionAgent(voiceGender: VoiceGender): AgentNode {
  return {
    id: 'under_writing_question_agent',
    name: 'Underwriting Question Agent',
    description: 'Collects underwriting answers; then routes to vehicle inspection',

    entryGuardrails: noEntryRequirements(),

    exitGuardrails: {
      transitions: new Map([
        ['go_to_inspection', {
          target: { agentId: 'vehicle_inspection_agent' },
          guardrails: []
        }],
        ['go_to_policy', {
          target: { agentId: 'policy_agent' },
          guardrails: []
        }],
        ['go_to_router', {
          target: { agentId: 'router_agent' },
          guardrails: []
        }]
      ]),
      fallback: { agentId: 'router_agent' }
    },

    initialPrompt: 'ابدأ أسئلة الاكتتاب.',

    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext,
      uiState?: UIStateManager
    ): Promise<ToolCallResult> => {
      logger.tool('[UnderWritingQuestionAgent] Tool called:', toolName, args);

      switch (toolName) {
        case 'go_to_router_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Returning to menu' },
            triggersTransition: true,
            transitionTo: 'router_agent',
            transitionData: { return_to_menu: true },
          };

        case 'go_to_policy_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Returning to policy flow' },
            triggersTransition: true,
            transitionTo: 'policy_agent',
            transitionData: {
              flow_state: context.transitionData.flow_state
            }
          };

        case 'go_to_vehicle_inspection_agent':
          logger.info('[UnderWritingQuestionAgent] Transitioning to vehicle_inspection_agent');
          const flowState = args.flow_state ?? context.transitionData.flow_state ?? {};
          return {
            toolName,
            args,
            result: { success: true, message: 'Starting vehicle inspection' },
            triggersTransition: true,
            transitionTo: 'vehicle_inspection_agent',
            transitionData: {
              caller_agent: 'policy_agent',
              flow_state: flowState
            }
          };

        case 'show_vehicle_usage_choice':
          if (uiState) uiState.setFlowStep('underwriting_vehicle_usage');
          return {
            toolName,
            args,
            result: { success: true, message: WAIT_MSG }
          };

        case 'show_had_accident_choice':
          if (uiState) uiState.setFlowStep('underwriting_had_accident');
          return {
            toolName,
            args,
            result: { success: true, message: WAIT_MSG }
          };

        case 'show_night_parking_choice':
          if (uiState) uiState.setFlowStep('underwriting_night_parking');
          return {
            toolName,
            args,
            result: { success: true, message: WAIT_MSG }
          };

        case 'show_violations_input':
          if (uiState) uiState.setFlowStep('underwriting_violations');
          return {
            toolName,
            args,
            result: { success: true, message: WAIT_MSG }
          };

        case 'show_tracking_device_choice':
          if (uiState) uiState.setFlowStep('underwriting_tracking_device');
          return {
            toolName,
            args,
            result: { success: true, message: WAIT_MSG }
          };

        case 'show_vehicle_financing_choice':
          if (uiState) uiState.setFlowStep('underwriting_vehicle_financing');
          return {
            toolName,
            args,
            result: { success: true, message: WAIT_MSG }
          };

        case 'show_number_of_drivers_input':
          if (uiState) uiState.setFlowStep('underwriting_number_of_drivers');
          return {
            toolName,
            args,
            result: { success: true, message: WAIT_MSG }
          };

        case 'search_faq':
          return await handleSearchFAQ(args, toolName);

        default:
          logger.warn('[UnderWritingQuestionAgent] Unknown tool:', toolName);
          return {
            toolName,
            args,
            result: { success: false, error: `Unknown tool: ${toolName}` }
          };
      }
    },

    onActivate: async (context: AgentContext) => {
      logger.info('[UnderWritingQuestionAgent] Activated');
    },

    onDeactivate: async (context: AgentContext) => {
      logger.info('[UnderWritingQuestionAgent] Deactivated');
    }
  };
}
